import React, { useCallback, useEffect, useState } from 'react';
import { Flex, Item, View, ProgressCircle, ListBox, Link } from '@adobe/react-spectrum';
import { truncateText } from '../../../../../utils/helperUtil';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../state/store';
import { useTranslation } from 'react-i18next';
import { ListRegisterRequest } from '../../../../../services/soap/features/requests/ListRegisterRequest';
import { ListProjectTypeRequest } from '../../../../../services/soap/project/requests/ListProjectTypeRequest';
import styles from './project_type_filter_component.module.css';

interface ProjectTypeFilterComponentProps {
  createParams: (filterType: any, filterValue: any) => void;
  filterParams?: any;
}

const ProjectTypeFilterComponent: React.FC<ProjectTypeFilterComponentProps> = ({ createParams, filterParams }) => {
  const { t } = useTranslation();
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const { store, projectService, featureService } = useDependency();
  const [toggleListVisibility, setToggleListVisibility] = useState<any>({ projectTypeList: true });
  const [projectTypeSelectedKey, setProjectTypeSelectedKey] = useState<Set<string>>(new Set(['All types']));
  const [projectTypeList, setProjectTypeList] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      setProjectTypeSelectedKey(new Set(['All types']));
      await getProjectType();
    })();
  }, [selectedApp]);

  const getProjectType = useCallback(async () => {
    try {
      let resListRegister = await featureService.listRegister(new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, +selectedApp));
      let projectActivityValue = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListProject')?.ROW.value ?? '';

      let resProjectType: any = await projectService.listProjectType(new ListProjectTypeRequest(store.Server, store.SessionId, projectActivityValue));
      if (resProjectType?.result === 'OK' && resProjectType?.count == 1) {
        let projectTypeArray: any = [];
        projectTypeArray.push({ id: resProjectType?.TYPE_LIST.TYPE.id, name: resProjectType?.TYPE_LIST.TYPE.name });
        setProjectTypeList(projectTypeArray);
      } else if (resProjectType?.result === 'OK' && resProjectType?.count > 1) {
        let projectTypeArray: any = [];
        resProjectType?.TYPE_LIST?.forEach((record: any) => {
          const { id, name } = record.TYPE;
          projectTypeArray.push({ id, name });
        });
        //projectTypeArray.unshift({ id: 'All types', name: 'All types' });
        setProjectTypeList(projectTypeArray);
      }
    } finally {
      setShowLoader(false);
    }
  }, [selectedApp]);

  const handleSelectionChange = (value: any) => {
    const arrValue = Array.from(value);
    const allProjectTypeCheck = arrValue.includes('All types') && arrValue.indexOf('All types') !== 0;

    if (allProjectTypeCheck) {
      const allProjectTypeSet = new Set(['All types']);
      setProjectTypeSelectedKey(allProjectTypeSet);
      createParams('type', allProjectTypeSet);
    } else {
      const filteredValue = arrValue.filter(item => item !== 'All types');
      const updateValue = filteredValue.length > 0 ? new Set(filteredValue) : new Set(['All types']);
      setProjectTypeSelectedKey(new Set(Array.from(updateValue).map(String)));
      createParams('type', updateValue);
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" height="100%" UNSAFE_style={{ backgroundColor: 'white' }} justifyContent={'center'} alignContent={'center'} alignItems={'center'}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'} minWidth={'200px'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_style={{ display: '' }}>
        <Flex direction={'column'} gap={'4px'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.project_type_container}>
          <Flex direction={'row'} height={'36px'} alignSelf={'stretch'} alignItems={'center'} justifyContent={'space-between'} UNSAFE_className={styles.project_type_heading}>
            <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
              <View position={'relative'} UNSAFE_className={styles.project_type_heading_text}>
                {t('project_type', { ns: 'layout_components' })}
              </View>
            </Flex>
            {toggleListVisibility.projectTypeList ? (
              <ChevronDown color="#B0BEC5" onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, projectTypeList: false }))} style={{ cursor: 'pointer' }} />
            ) : (
              <ChevronUp onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, projectTypeList: true }))} style={{ cursor: 'pointer' }} />
            )}
          </Flex>
          <Flex
            direction={'column'}
            alignSelf={'stretch'}
            alignItems={'start'}
            justifyContent={'start'}
            UNSAFE_className={styles.project_type_component}
            UNSAFE_style={{ display: toggleListVisibility.projectTypeList ? '' : 'none' }}
            marginStart={'-5px'}
          >
            <ListBox
              width="100%"
              selectionMode="multiple"
              defaultSelectedKeys={projectTypeSelectedKey}
              selectedKeys={projectTypeSelectedKey}
              items={projectTypeList}
              onSelectionChange={value => {
                handleSelectionChange(value);
              }}
              aria-label="none"
            >
              {(item: any) => <Item key={item.id}>{truncateText(item.name, 35)}</Item>}
            </ListBox>
            <View position={'relative'} width={'100%'} height={'20px'} UNSAFE_className={styles.project_type_clear_all_container}>
              <View position={'relative'} UNSAFE_className={styles.project_type_clear_all_text}>
                <Link
                  onPress={() => {
                    handleSelectionChange(new Set(['All types']));
                  }}
                >
                  {t('clear_all', { ns: 'layout_components' })}
                </Link>
              </View>
            </View>
          </Flex>
        </Flex>
      </Flex>
    );
  }
};

export default ProjectTypeFilterComponent;
